import axios from 'axios';
import { BackOffice, RohtasAuthHttp } from 'utils/http';
import { authSdk } from 'lib/auth';
import { getRealm, setAccessToken, setRefreshToken } from './storage';

const normalizeCopilotError = (e) => ({
  response: {
    error: true,
    data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
    message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
    code: e?.response?.status || 404,
  },
});

const normalizeXPayUser = (user = {}) => ({
  ...user,
  id: user?.id || user?.userId || user?._id || '',
  userId: user?.userId || user?.id || user?._id || '',
  firstName: user?.firstName || user?.first_name || '',
  lastName: user?.lastName || user?.last_name || '',
  email: user?.email || '',
  roles: user?.roles || user?.userRoles || [],
  createdTimestamp: user?.createdTimestamp || user?.createdAt || user?.created_at,
  enabled: typeof user?.enabled === 'boolean' ? user.enabled : user?.status === 'active',
});

export const refreshToken = async () => {
  try {
    const realm = getRealm();
    const response = await authSdk.refreshToken(realm ? { realm } : undefined);
    const accessToken = response?.access_token;

    if (!accessToken) {
      throw new Error('No access token returned from auth SDK refresh');
    }

    setAccessToken(accessToken);

    if (response?.refresh_token) {
      setRefreshToken(response.refresh_token);
    }

    return accessToken;
  } catch (err) {
    throw err;
  }
};

export const login = async (payload) => {
  try {
    const response = await BackOffice.post('/v1/user/login', payload);
    return response;
  } catch (e) {
    console.log('eee', JSON.stringify(e));

    throw normalizeCopilotError(e);
  }
};

export const forgotPassword = async (payload) => {
  try {
    const response = await BackOffice.post('/v1/user/password/forgot', payload);
    return response;
  } catch (e) {
    throw normalizeCopilotError(e);
  }
};

export const getXPayUsers = async (id) => {
  try {
    const realm = id || getRealm();
    const response = await RohtasAuthHttp.get('/tenant/users', realm ? { params: { realm } } : undefined);
    const raw = response?.data?.data?.users;
    const normalizedUsers = (raw || []).map(normalizeXPayUser);

    return {
      data: normalizedUsers,
    };
  } catch (e) {
    throw normalizeCopilotError(e);
  }
};

export const createXPayUser = async (payload) => {
  try {
    const requestBody = {
      ...payload,
      realm: payload?.realm || getRealm(),
    };
    const response = await RohtasAuthHttp.post('/tenant/users', requestBody);
    return response?.data;
  } catch (e) {
    throw normalizeCopilotError(e);
  }
};

export const updateXPayUser = async (payload) => {
  try {
    const userId = payload?.userId || payload?.id;

    if (!userId) {
      throw {
        response: {
          error: true,
          data: 'User id is required.',
          message: 'User id is required.',
          code: 400,
        },
      };
    }

    const requestBody = {};
    const realm = payload?.realm || getRealm();

    if (payload?.firstName !== undefined) requestBody.firstName = payload.firstName;
    if (payload?.lastName !== undefined) requestBody.lastName = payload.lastName;
    if (Array.isArray(payload?.roles)) requestBody.roles = payload.roles;
    if (realm) requestBody.realm = realm;
    if (payload?.status !== undefined) {
      requestBody.status = typeof payload.status === 'boolean' ? (payload.status ? 'active' : 'inactive') : payload.status;
    }

    const response = await RohtasAuthHttp.put(`/tenant/users/${userId}`, requestBody);
    return response?.data;
  } catch (e) {
    throw e?.response?.error ? e : normalizeCopilotError(e);
  }
};

export const addXPayUserRole = async (userRoles) => {
  try {
    const response = await BackOffice.post('/v1/user/role/add', userRoles);
    return response;
  } catch (e) {
    throw normalizeCopilotError(e);
  }
};

export const deleteXPayUserRole = async (deleteUserRole) => {
  try {
    const response = await BackOffice.delete('/v1/user/role', {
      data: deleteUserRole,
    });
    return response;
  } catch (e) {
    throw normalizeCopilotError(e);
  }
};

export const resetPassword = async (payload, authToken) => {
  try {
    const response = await axios.post(`${import.meta.env.VITE_APP_BACKOFFICE_API}/public/v1/user/password/reset`, payload, {
      headers: {
        Authorization: authToken,
      },
    });
    return response;
  } catch (e) {
    throw normalizeCopilotError(e);
  }
};
